import React, { useState, useEffect } from 'react';
import {
  Star,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  GripVertical,
  Edit2,
  Check,
  X,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from 'sonner';

const CATEGORIES = [
  'Voicemail',
  'Forwarding',
  'DND & Privacy',
  'Call Queue',
  'Recording',
  'Call Parking',
  'Pickup',
  'Utilities',
  'Auto Attendant',
  'Conferencing',
  'Hot Desk',
  'Other'
];

const ROLES = [
  { value: 'standard', label: 'Standard' },
  { value: 'phone_admin', label: 'Phone Admin' },
  { value: 'customer_admin', label: 'Customer Admin' }
];

export default function StarCodesEditor({
  codes,
  onSave,
  onReset,
  onInitialize,
  loading,
  saving,
  initialized
}) {
  const [editedCodes, setEditedCodes] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [newCode, setNewCode] = useState({
    code: '',
    name: '',
    description: '',
    category: 'Other',
    requires_input: false,
    input_placeholder: 'Number',
    visible_to: ['standard', 'phone_admin', 'customer_admin']
  });

  // Sync editedCodes with codes prop when it changes
  React.useEffect(() => {
    setEditedCodes(codes || []);
    // Expand all categories by default
    const expanded = {};
    (codes || []).forEach(c => {
      expanded[c.category || 'Other'] = true;
    });
    setExpandedCategories(expanded);
  }, [codes]);

  const groupedCodes = editedCodes.reduce((acc, code) => {
    const cat = code.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(code);
    return acc;
  }, {});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleAddCode = () => {
    if (!newCode.code || !newCode.name) {
      toast.error('Code and name are required');
      return;
    }
    
    const codeToAdd = {
      ...newCode,
      id: `new-${Date.now()}`,
      order: editedCodes.length
    };
    
    setEditedCodes([...editedCodes, codeToAdd]);
    setShowAddDialog(false);
    setNewCode({
      code: '',
      name: '',
      description: '',
      category: 'Other',
      requires_input: false,
      input_placeholder: 'Number',
      visible_to: ['standard', 'phone_admin', 'customer_admin']
    });
    toast.success('Code added. Remember to save changes.');
  };

  const handleDeleteCode = (codeId) => {
    setEditedCodes(editedCodes.filter(c => c.id !== codeId));
    toast.success('Code removed. Remember to save changes.');
  };

  const handleEditCode = (code) => {
    setEditingCode({ ...code });
  };

  const handleSaveEdit = () => {
    setEditedCodes(editedCodes.map(c => 
      c.id === editingCode.id ? editingCode : c
    ));
    setEditingCode(null);
    toast.success('Code updated. Remember to save changes.');
  };

  const handleSave = async () => {
    try {
      await onSave(editedCodes);
      toast.success('Star codes saved successfully');
    } catch (error) {
      toast.error('Failed to save star codes');
    }
  };

  const handleReset = async () => {
    if (window.confirm('This will replace your current star codes with the provider defaults. Any custom codes will be lost. Continue?')) {
      try {
        await onReset();
        toast.success('Star codes copied from defaults');
      } catch (error) {
        toast.error('Failed to copy defaults');
      }
    }
  };

  const handleInitialize = async () => {
    try {
      await onInitialize();
      toast.success('Star codes initialized');
    } catch (error) {
      toast.error('Failed to initialize star codes');
    }
  };

  const toggleRoleVisibility = (code, role) => {
    const visible = code.visible_to || [];
    const newVisible = visible.includes(role)
      ? visible.filter(r => r !== role)
      : [...visible, role];
    
    if (editingCode && editingCode.id === code.id) {
      setEditingCode({ ...editingCode, visible_to: newVisible });
    } else {
      setEditedCodes(editedCodes.map(c =>
        c.id === code.id ? { ...c, visible_to: newVisible } : c
      ));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!initialized) {
    return (
      <Card className="border-border/50">
        <CardContent className="py-12 text-center">
          <Star className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Star Codes Not Initialized</h3>
          <p className="text-muted-foreground mb-6">
            Initialize star codes with provider defaults to get started.
          </p>
          <Button onClick={handleInitialize} disabled={saving}>
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Initialize Star Codes
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline">{editedCodes.length} codes</Badge>
          <Badge variant="outline">{Object.keys(groupedCodes).length} categories</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Code
          </Button>
          <Button variant="outline" size="sm" onClick={handleReset} disabled={saving}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Copy from Defaults
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Codes by Category */}
      <div className="space-y-2">
        {Object.entries(groupedCodes).sort(([a], [b]) => a.localeCompare(b)).map(([category, categoryCodes]) => (
          <Collapsible
            key={category}
            open={expandedCategories[category]}
            onOpenChange={() => toggleCategory(category)}
          >
            <Card className="border-border/50">
              <CollapsibleTrigger asChild>
                <CardHeader className="py-3 cursor-pointer hover:bg-secondary/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {expandedCategories[category] ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                      <CardTitle className="text-sm font-medium">{category}</CardTitle>
                      <Badge variant="secondary" className="text-xs">
                        {categoryCodes.length}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {categoryCodes.map((code) => (
                      <div
                        key={code.id}
                        className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
                      >
                        <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                        <code className="font-mono text-sm font-medium w-16">{code.code}</code>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{code.name}</p>
                          {code.description && (
                            <p className="text-xs text-muted-foreground truncate">{code.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {code.requires_input && (
                            <Badge variant="outline" className="text-xs">Input</Badge>
                          )}
                          {(code.visible_to || []).map(role => (
                            <Badge key={role} variant="secondary" className="text-xs">
                              {role === 'customer_admin' ? 'Admin' : role === 'phone_admin' ? 'Phone' : 'Std'}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEditCode(code)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteCode(code.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}
      </div>

      {/* Add Code Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Star Code</DialogTitle>
            <DialogDescription>
              Add a new star code for this company
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  placeholder="*72"
                  value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={newCode.category}
                  onValueChange={(value) => setNewCode({ ...newCode, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Forward All Calls"
                value={newCode.name}
                onChange={(e) => setNewCode({ ...newCode, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Optional description"
                value={newCode.description}
                onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={newCode.requires_input}
                onCheckedChange={(checked) => setNewCode({ ...newCode, requires_input: checked })}
              />
              <Label className="cursor-pointer">Requires additional input</Label>
            </div>
            {newCode.requires_input && (
              <div className="space-y-2">
                <Label>Input Placeholder</Label>
                <Input
                  placeholder="Number"
                  value={newCode.input_placeholder}
                  onChange={(e) => setNewCode({ ...newCode, input_placeholder: e.target.value })}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Visible to</Label>
              <div className="flex gap-4">
                {ROLES.map(role => (
                  <div key={role.value} className="flex items-center gap-2">
                    <Checkbox
                      checked={(newCode.visible_to || []).includes(role.value)}
                      onCheckedChange={(checked) => {
                        const visible = newCode.visible_to || [];
                        setNewCode({
                          ...newCode,
                          visible_to: checked
                            ? [...visible, role.value]
                            : visible.filter(r => r !== role.value)
                        });
                      }}
                    />
                    <Label className="text-sm cursor-pointer">{role.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCode}>
              <Plus className="w-4 h-4 mr-2" />
              Add Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Code Dialog */}
      <Dialog open={!!editingCode} onOpenChange={(open) => !open && setEditingCode(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Star Code</DialogTitle>
            <DialogDescription>
              Modify the star code configuration
            </DialogDescription>
          </DialogHeader>
          {editingCode && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Code *</Label>
                  <Input
                    value={editingCode.code}
                    onChange={(e) => setEditingCode({ ...editingCode, code: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={editingCode.category}
                    onValueChange={(value) => setEditingCode({ ...editingCode, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={editingCode.name}
                  onChange={(e) => setEditingCode({ ...editingCode, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editingCode.description || ''}
                  onChange={(e) => setEditingCode({ ...editingCode, description: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={editingCode.requires_input}
                  onCheckedChange={(checked) => setEditingCode({ ...editingCode, requires_input: checked })}
                />
                <Label className="cursor-pointer">Requires additional input</Label>
              </div>
              {editingCode.requires_input && (
                <div className="space-y-2">
                  <Label>Input Placeholder</Label>
                  <Input
                    value={editingCode.input_placeholder || 'Number'}
                    onChange={(e) => setEditingCode({ ...editingCode, input_placeholder: e.target.value })}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Visible to</Label>
                <div className="flex gap-4">
                  {ROLES.map(role => (
                    <div key={role.value} className="flex items-center gap-2">
                      <Checkbox
                        checked={(editingCode.visible_to || []).includes(role.value)}
                        onCheckedChange={() => toggleRoleVisibility(editingCode, role.value)}
                      />
                      <Label className="text-sm cursor-pointer">{role.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCode(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
